import React from "react";
import { Plane, Stethoscope, Car, Check } from "lucide-react";
import { Reveal, MaskReveal } from "./Reveal";
import { SERVICES, ADD_ONS, IMAGES } from "../../lib/site";

const ICONS = { airport: Plane, medical: Stethoscope, personal: Car };

const ServiceCard = ({ s, delay }) => {
  const Icon = ICONS[s.id];
  return (
    <Reveal
      delay={delay}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#111111] ${
        s.featured ? "lg:col-span-3 lg:row-span-2" : "lg:col-span-2"
      }`}
      data-testid={`service-card-${s.id}`}
    >
      <div className={`absolute inset-0 overflow-hidden ${s.featured ? "" : ""}`}>
        <img
          src={IMAGES[s.image]}
          alt={s.title}
          className="w-full h-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-[opacity,transform] duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]/30" />
      </div>

      <div className={`relative z-10 flex flex-col ${s.featured ? "p-9 lg:p-11 min-h-[30rem]" : "p-8 min-h-[15rem]"}`}>
        <div className="flex items-start justify-between">
          <div className="h-12 w-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-colors duration-400">
            <Icon size={20} />
          </div>
          <span className="font-serif-display text-4xl text-white/10">{s.num}</span>
        </div>

        <div className="mt-auto pt-8">
          <span className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">{s.short}</span>
          <h3 className={`font-serif-display text-white mt-2 ${s.featured ? "text-4xl" : "text-2xl"}`}>{s.title}</h3>
          <p className="text-neutral-400 mt-3 leading-relaxed max-w-md">{s.desc}</p>

          {s.featured && (
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
              {s.points.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-neutral-300">
                  <Check size={15} className="text-[#D4AF37]" /> {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Reveal>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold mb-5">
              <span className="h-px w-10 bg-[#D4AF37]" /> What We Offer
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.02]">
              <MaskReveal lines={["Rides for every", "part of life."]} />
            </h2>
          </div>
          <Reveal delay={0.2}>
            <p className="text-neutral-400 max-w-sm md:text-right leading-relaxed">
              From early-morning airport runs to the doctor's office and everyday errands — one trusted number for all of it.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 auto-rows-fr">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} delay={i * 0.1} />
          ))}
        </div>

        <Reveal delay={0.15} className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d0d] p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="md:w-1/4">
              <h3 className="font-serif-display text-2xl text-white">Also available</h3>
              <p className="text-neutral-500 text-sm mt-1">Just ask when you book.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {ADD_ONS.map((a) => (
                <span
                  key={a}
                  data-testid={`addon-${a}`}
                  className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-5 py-2.5 text-sm text-neutral-200 hover:border-[#D4AF37] transition-colors duration-300"
                >
                  {a}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
